import { useState, useEffect } from 'react';
import { BarChart3, Euro, FileText, CheckSquare, ClipboardCheck, Building2, UserCheck, Calendar, ArrowUp, ArrowDown, Filter, Search, TrendingUp, DollarSign, Users, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Collab, getDocuments, updateDocument } from '../lib/supabaseHelpers';
import { Influencer, normalizeInfluencer } from '../lib/supabase';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { Dialog } from '@headlessui/react';
import InfluencerAvatar from '../components/InfluencerAvatar';
import { useAuth } from '../contexts/AuthContextSupabase';

// Composant pour le calendrier de CA mensuel
const MonthlyRevenueCalendar = ({ collabs, timeRange, startDate, endDate, onMonthChange }: {
  collabs: Collab[],
  timeRange: 'current' | 'all' | '3months' | 'custom',
  startDate: string,
  endDate: string,
  onMonthChange?: (month: Date) => void
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState<{ date: Date; revenue: number; collabs: any[] } | null>(null);
  const [dailyRevenue, setDailyRevenue] = useState<Map<string, { revenue: number; collabs: any[] }>>(new Map());

  useEffect(() => {
    const revenueByDay = new Map<string, { revenue: number; collabs: any[] }>();

    collabs.forEach(collab => {
      let collabDate: Date;
      const rawDate = (collab as any).signed_date || collab.date;
      if (typeof rawDate === 'string') {
        collabDate = new Date(rawDate);
      } else if (rawDate && 'seconds' in rawDate) {
        collabDate = new Date(rawDate.seconds * 1000);
      } else if (rawDate) {
        collabDate = new Date(rawDate);
      } else {
        return;
      }

      const dayKey = `${collabDate.getFullYear()}-${String(collabDate.getMonth() + 1).padStart(2, '0')}-${String(collabDate.getDate()).padStart(2, '0')}`;

      const revenue = collab.influencers?.reduce((sum, inf) =>
        sum + parseFloat(inf.rate || '0'), 0) || 0;

      if (!isNaN(revenue)) {
        const dayData = revenueByDay.get(dayKey) || { revenue: 0, collabs: [] };
        dayData.revenue += revenue;
        dayData.collabs.push({ ...collab, dailyRevenue: revenue });
        revenueByDay.set(dayKey, dayData);
      }
    });

    setDailyRevenue(revenueByDay);
  }, [collabs]);

  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    const day = new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    return day === 0 ? 6 : day - 1;
  };

  const previousMonth = () => {
    const newMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1);
    setCurrentMonth(newMonth);
    onMonthChange?.(newMonth);
  };

  const nextMonth = () => {
    const newMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1);
    setCurrentMonth(newMonth);
    onMonthChange?.(newMonth);
  };

  const getDayRevenue = (day: number) => {
    const dayKey = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return dailyRevenue.get(dayKey);
  };

  const handleDayClick = (day: number) => {
    const dayData = getDayRevenue(day);
    if (dayData && dayData.collabs.length > 0) {
      setSelectedDay({
        date: new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day),
        revenue: dayData.revenue,
        collabs: dayData.collabs
      });
    }
  };

  const daysInMonth = getDaysInMonth(currentMonth);
  const firstDayOfMonth = getFirstDayOfMonth(currentMonth);
  const monthName = currentMonth.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  const monthRevenue = Array.from({ length: daysInMonth }, (_, i) => getDayRevenue(i + 1)?.revenue || 0).reduce((a, b) => a + b, 0);

  return (
    <div className="rounded-2xl border border-white/[0.08] bg-white/[0.04] p-8">
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-blue-400" />
            </div>
            <h2 className="text-xl font-bold text-white">
              Chiffre d'affaires
            </h2>
          </div>
          <p className="text-sm text-white/40 ml-13">
            Toutes les collaborations
          </p>
        </div>
        <div className="text-right">
          <div className="text-3xl font-bold text-blue-400">
            {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(monthRevenue)}
          </div>
          <p className="text-xs text-white/30 mt-1">Revenus du mois</p>
        </div>
      </div>

      <div className="mt-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-white capitalize">{monthName}</h3>
          <div className="flex items-center gap-2">
            <button
              onClick={previousMonth}
              className="p-2 hover:bg-white/[0.08] rounded-lg transition-colors"
              aria-label="Mois précédent"
            >
              <ChevronLeft className="w-5 h-5 text-white/50" />
            </button>
            <button
              onClick={nextMonth}
              className="p-2 hover:bg-white/[0.08] rounded-lg transition-colors"
              aria-label="Mois suivant"
            >
              <ChevronRight className="w-5 h-5 text-white/50" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-2">
          {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((day) => (
            <div key={day} className="text-center text-xs font-semibold text-white/30 pb-2">
              {day}
            </div>
          ))}

          {Array.from({ length: firstDayOfMonth }).map((_, i) => (
            <div key={`empty-${i}`} className="aspect-square" />
          ))}

          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1;
            const dayData = getDayRevenue(day);
            const hasCollabs = dayData && dayData.collabs.length > 0;
            const hasRevenue = dayData && dayData.revenue > 0;

            return (
              <button
                key={day}
                onClick={() => handleDayClick(day)}
                disabled={!hasCollabs}
                className={`aspect-square rounded-lg p-2 transition-all ${
                  hasCollabs
                    ? 'bg-blue-500/[0.15] hover:bg-blue-500/25 border border-blue-500/30 cursor-pointer'
                    : 'bg-white/[0.03] border border-white/[0.06] cursor-default'
                }`}
              >
                <div className="flex flex-col items-center justify-center h-full">
                  <span className={`text-sm font-semibold ${hasCollabs ? 'text-blue-300' : 'text-white/20'}`}>
                    {day}
                  </span>
                  {hasCollabs && (
                    <span className="text-xs font-bold text-blue-400 mt-1">
                      {hasRevenue ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(dayData.revenue) : `${dayData.collabs.length} collab${dayData.collabs.length > 1 ? 's' : ''}`}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {selectedDay && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#141414] border border-white/[0.08] rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="bg-blue-500/10 border-b border-white/[0.08] p-6 flex items-center justify-between">
              <div>
                <h3 className="text-2xl font-bold text-white">
                  {selectedDay.date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                </h3>
                <p className="text-blue-400 mt-1 text-lg font-semibold">
                  Total : {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(selectedDay.revenue)}
                </p>
              </div>
              <button
                onClick={() => setSelectedDay(null)}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-6 h-6 text-white/60" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-96">
              <h4 className="text-[10px] font-semibold text-white/40 uppercase tracking-widest mb-4">
                {selectedDay.collabs.length} Collaboration{selectedDay.collabs.length > 1 ? 's' : ''}
              </h4>
              <div className="space-y-3">
                {selectedDay.collabs.map((collab, index) => {
                  const collabRevenue = collab.dailyRevenue || 0;

                  const getStatusInfo = () => {
                    if (collab.payment_received) {
                      return { label: 'Paiement reçu', color: 'bg-blue-500/20 text-blue-400', icon: Euro };
                    } else if (collab.contract_signed) {
                      return { label: 'Contrat signé', color: 'bg-emerald-500/20 text-emerald-400', icon: CheckSquare };
                    } else if (collab.quote_sent) {
                      return { label: 'Devis envoyé', color: 'bg-amber-500/20 text-amber-400', icon: ClipboardCheck };
                    } else {
                      return { label: 'En attente', color: 'bg-white/10 text-white/50', icon: FileText };
                    }
                  };

                  const status = getStatusInfo();
                  const StatusIcon = status.icon;

                  return (
                    <div
                      key={collab.id || index}
                      className="rounded-xl p-4 border border-white/[0.08] bg-white/[0.04] hover:border-white/[0.16] transition-all"
                    >
                      <div className="space-y-3">
                        {collab.influencers?.map((inf: any, infIndex: number) => {
                          const influencerName = (inf as any).display_name || (inf as any).full_name || inf.fullName || `Influenceur #${infIndex + 1}`;
                          const profileImage = inf.profile_image_url || collab.organization_influencers?.profile_image_url;

                          return (
                            <div key={infIndex} className="flex items-center gap-3">
                              <InfluencerAvatar
                                profileImageUrl={profileImage}
                                name={influencerName}
                                size="md"
                              />
                              <div className="flex-1">
                                <p className="font-semibold text-white">{influencerName}</p>
                                <p className="text-sm text-white/40">
                                  {inf.rate ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(parseFloat(inf.rate)) : 'Tarif non défini'}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/[0.06]">
                        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-semibold ${status.color}`}>
                          <StatusIcon className="w-4 h-4" />
                          {status.label}
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-blue-400">
                            {collabRevenue > 0 ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(collabRevenue) : '—'}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-white/[0.03] p-4 border-t border-white/[0.06]">
              <button
                onClick={() => setSelectedDay(null)}
                className="w-full bg-white text-black font-semibold py-3 rounded-xl transition-colors hover:bg-white/90"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Composant pour les statuts des collaborations
const CollaborationStatusCards = ({ collabs }: { collabs: Collab[] }) => {
  const pending = collabs.filter(c => !c.quote_sent && !c.contract_signed).length;
  const quoteSent = collabs.filter(c => c.quote_sent && !c.contract_signed).length;
  const contractSigned = collabs.filter(c => c.contract_signed && !c.payment_received).length;
  const paymentReceived = collabs.filter(c => c.payment_received).length;

  const statusCards = [
    {
      title: 'En attente',
      count: pending,
      icon: FileText,
      gradient: 'from-white/40 to-white/20',
      bgColor: '',
      iconBg: 'bg-white/10',
      iconColor: 'text-white/50'
    },
    {
      title: 'Devis envoyé',
      count: quoteSent,
      icon: ClipboardCheck,
      gradient: 'from-amber-400 to-amber-500',
      bgColor: '',
      iconBg: 'bg-amber-500/20',
      iconColor: 'text-amber-400'
    },
    {
      title: 'Contrat signé',
      count: contractSigned,
      icon: CheckSquare,
      gradient: 'from-emerald-400 to-emerald-500',
      bgColor: '',
      iconBg: 'bg-emerald-500/20',
      iconColor: 'text-emerald-400'
    },
    {
      title: 'Paiement reçu',
      count: paymentReceived,
      icon: Euro,
      gradient: 'from-blue-400 to-blue-500',
      bgColor: '',
      iconBg: 'bg-blue-500/20',
      iconColor: 'text-blue-400'
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {statusCards.map((card, index) => (
        <div
          key={index}
          className="rounded-2xl p-6 border border-white/[0.08] bg-white/[0.04] hover:border-white/[0.16] transition-all cursor-pointer group"
        >
          <div className="flex items-start justify-between mb-4">
            <div className={`w-12 h-12 rounded-xl ${card.iconBg} flex items-center justify-center group-hover:scale-110 transition-transform`}>
              <card.icon className={`w-6 h-6 ${card.iconColor}`} />
            </div>
            <div className={`text-4xl font-bold bg-gradient-to-r ${card.gradient} bg-clip-text text-transparent`}>
              {card.count}
            </div>
          </div>
          <h3 className="text-base font-semibold text-white mb-1">
            {card.title}
          </h3>
          <div className={`h-1 w-16 rounded-full bg-gradient-to-r ${card.gradient}`} />
        </div>
      ))}
    </div>
  );
};

// Diagramme circulaire SVG simple
const DonutChart = ({ data }: { data: { label: string; value: number; color: string }[] }) => {
  const total = data.reduce((sum, d) => sum + d.value, 0);
  if (total === 0) return <div className="flex items-center justify-center h-40 text-white/30 text-sm">Aucune donnée</div>;

  let cumulative = 0;
  const radius = 60;
  const cx = 80;
  const cy = 80;
  const gap = 2;

  const slices = data.filter(d => d.value > 0).map(d => {
    const startAngle = (cumulative / total) * 2 * Math.PI - Math.PI / 2;
    cumulative += d.value;
    const endAngle = (cumulative / total) * 2 * Math.PI - Math.PI / 2;
    const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;
    const x1 = cx + radius * Math.cos(startAngle + gap / radius);
    const y1 = cy + radius * Math.sin(startAngle + gap / radius);
    const x2 = cx + radius * Math.cos(endAngle - gap / radius);
    const y2 = cy + radius * Math.sin(endAngle - gap / radius);
    const innerRadius = 36;
    const xi1 = cx + innerRadius * Math.cos(startAngle + gap / innerRadius);
    const yi1 = cy + innerRadius * Math.sin(startAngle + gap / innerRadius);
    const xi2 = cx + innerRadius * Math.cos(endAngle - gap / innerRadius);
    const yi2 = cy + innerRadius * Math.sin(endAngle - gap / innerRadius);
    return { ...d, path: `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} L ${xi2} ${yi2} A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${xi1} ${yi1} Z` };
  });

  return (
    <div className="flex items-center gap-6">
      <svg width="160" height="160" viewBox="0 0 160 160">
        {slices.map((s, i) => (
          <path key={i} d={s.path} fill={s.color} className="hover:opacity-80 transition-opacity cursor-default" />
        ))}
        <text x={cx} y={cy - 6} textAnchor="middle" fill="rgba(255,255,255,0.9)" fontSize="13" fontWeight="700">{total}</text>
        <text x={cx} y={cy + 10} textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize="10">collabs</text>
      </svg>
      <div className="flex flex-col gap-2 flex-1">
        {data.filter(d => d.value > 0).map((d, i) => (
          <div key={i} className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: d.color }} />
              <span className="text-sm text-white/60 font-medium">{d.label}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-white">{d.value}</span>
              <span className="text-xs text-white/30">({Math.round(d.value / total * 100)}%)</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Composant répartition des types de collaboration
const CollabTypeChart = ({ collabs }: { collabs: Collab[] }) => {
  const typeConfig: Record<string, { label: string; color: string }> = {
    collaboration_entrante: { label: 'Entrante', color: '#3b82f6' },
    collaboration_sortante: { label: 'Sortante', color: '#10b981' },
    casting_interne: { label: 'Casting interne', color: '#f59e0b' },
    casting_externe: { label: 'Casting externe', color: '#ef4444' },
    null: { label: 'Non défini', color: '#94a3b8' },
  };

  const counts: Record<string, number> = {};
  collabs.forEach(c => {
    const key = (c as any).type || 'null';
    counts[key] = (counts[key] || 0) + 1;
  });

  const data = Object.entries(typeConfig).map(([key, cfg]) => ({
    label: cfg.label,
    value: counts[key] || 0,
    color: cfg.color,
  }));

  return (
    <div className="rounded-2xl border border-white/[0.08] bg-white/[0.04] p-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
          <BarChart3 className="w-5 h-5 text-blue-400" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-white">Répartition des collaborations</h2>
          <p className="text-sm text-white/40">Par type de collaboration</p>
        </div>
      </div>
      <DonutChart data={data} />
    </div>
  );
};

// Composant top marques
const TopBrandsChart = ({ collabs }: { collabs: Collab[] }) => {
  const brandMap = new Map<string, number>();
  collabs.forEach(c => {
    const brand = (c.brand || '').trim();
    if (!brand) return;
    const budget = parseFloat((c as any).budget || '0');
    if (isNaN(budget) || budget <= 0) return;
    brandMap.set(brand, (brandMap.get(brand) || 0) + budget);
  });

  const brands = Array.from(brandMap.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 7);

  const maxBudget = brands[0]?.[1] || 1;

  const barColors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4'];

  return (
    <div className="rounded-2xl border border-white/[0.08] bg-white/[0.04] p-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
          <Building2 className="w-5 h-5 text-emerald-400" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-white">Top marques</h2>
          <p className="text-sm text-white/40">Budget investi par marque</p>
        </div>
      </div>
      {brands.length === 0 ? (
        <div className="flex items-center justify-center h-40 text-white/30 text-sm">Aucune donnée</div>
      ) : (
        <div className="space-y-3">
          {brands.map(([brand, budget], i) => (
            <div key={brand} className="flex items-center gap-3">
              <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0" style={{ backgroundColor: barColors[i % barColors.length] }}>
                {i + 1}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-semibold text-white/70 truncate">{brand}</span>
                  <span className="text-sm font-bold text-white ml-2 flex-shrink-0">
                    {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(budget)}
                  </span>
                </div>
                <div className="h-2 bg-white/[0.06] rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${(budget / maxBudget) * 100}%`, backgroundColor: barColors[i % barColors.length] }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Composant pour le tableau des influenceurs
const InfluencerCollaborationsTable = ({ collabs, influencers }: { collabs: Collab[], influencers: Influencer[] }) => {
  const navigate = useNavigate();
  const [sortField, setSortField] = useState<'name' | 'count'>('count');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [search, setSearch] = useState('');
  const [selectedInfluencer, setSelectedInfluencer] = useState<{
    id: string;
    name: string;
    profile_image_url?: string | null;
  } | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const getInfluencerCollaborations = () => {
    const influencerMap = new Map<string, {
      id: string,
      name: string,
      count: number,
      revenue: number,
      fullNames: Set<string>,
      collabIds: Set<string>,
      profile_image_url?: string | null
    }>();

    influencers.forEach(inf => {
      if (inf.id) {
        influencerMap.set(inf.id, {
          id: inf.id,
          name: inf.fullName,
          count: 0,
          revenue: 0,
          fullNames: new Set([inf.fullName.toLowerCase().trim()]),
          collabIds: new Set(),
          profile_image_url: inf.profile_image_url
        });
      }
    });

    const nameToIdMap = new Map<string, string>();
    influencers.forEach(inf => {
      if (inf.id && inf.fullName) {
        nameToIdMap.set(inf.fullName.toLowerCase().trim(), inf.id);
      }
    });

    collabs.forEach(collab => {
      if (!Array.isArray(collab.influencers)) {
        console.warn('collab.influencers is not an array:', collab);
        return;
      }

      collab.influencers.forEach(inf => {
        const infFullName = (inf as any).display_name || (inf as any).full_name || inf.fullName;

        if (!infFullName?.trim()) {
          return;
        }

        const normalizedName = infFullName.trim().toLowerCase();
        let influencerId = inf.id;

        if (!influencerId && nameToIdMap.has(normalizedName)) {
          influencerId = nameToIdMap.get(normalizedName);
        }

        if (influencerId && influencerMap.has(influencerId)) {
          const data = influencerMap.get(influencerId)!;
          if (!data.collabIds.has(collab.id!)) {
            data.count += 1;
            data.collabIds.add(collab.id!);
          }
          const rate = parseFloat(inf.rate || '0');
          if (!isNaN(rate)) {
            data.revenue += rate;
          }
          data.fullNames.add(normalizedName);
        }
      });
    });

    collabs.forEach(collab => {
      if (!Array.isArray(collab.influencers)) {
        return;
      }

      collab.influencers.forEach(inf => {
        const infFullName = (inf as any).display_name || (inf as any).full_name || inf.fullName;

        if (!infFullName?.trim() || inf.id) {
          return;
        }

        const normalizedName = infFullName.trim().toLowerCase();
        let matched = false;

        for (const [id, data] of influencerMap.entries()) {
          if (data.fullNames.has(normalizedName)) {
            matched = true;
            break;
          }

          for (const knownName of data.fullNames) {
            if (normalizedName.includes(knownName) || knownName.includes(normalizedName)) {
              data.fullNames.add(normalizedName);
              if (!data.collabIds.has(collab.id!)) {
                data.count += 1;
                data.collabIds.add(collab.id!);
              }
              const rate = parseFloat(inf.rate || '0');
              if (!isNaN(rate)) {
                data.revenue += rate;
              }
              matched = true;
              break;
            }
          }

          if (matched) break;
        }

        if (!matched) {
          const tempId = `temp-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
          influencerMap.set(tempId, {
            id: tempId,
            name: infFullName.trim(),
            count: 1,
            collabIds: new Set([collab.id!]),
            revenue: isNaN(parseFloat(inf.rate || '0')) ? 0 : parseFloat(inf.rate || '0'),
            fullNames: new Set([normalizedName])
          });
        }
      });
    });

    return Array.from(influencerMap.values())
      .filter(item => item.count > 0)
      .filter(item => item.name.toLowerCase().includes(search.toLowerCase()))
      .sort((a, b) => {
        if (sortField === 'name') {
          return sortDirection === 'asc'
            ? a.name.localeCompare(b.name)
            : b.name.localeCompare(a.name);
        } else {
          const countDiff = sortDirection === 'asc'
            ? a.count - b.count
            : b.count - a.count;
          if (countDiff !== 0) return countDiff;
          return sortDirection === 'asc' ? a.revenue - b.revenue : b.revenue - a.revenue;
        }
      });
  };

  const influencerCollaborations = getInfluencerCollaborations();

  const toggleSort = (field: 'name' | 'count') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  return (
    <div className="rounded-2xl border border-white/[0.08] bg-white/[0.04]">
      <div className="p-8 border-b border-white/[0.06]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">
                Top créateurs
              </h2>
              <p className="text-sm text-white/40">Par nombre de collaborations</p>
            </div>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/30" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 pr-4 py-2.5 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none transition-all"
            />
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/[0.06] bg-white/[0.02]">
              <th
                className="text-left py-4 px-8 text-[10px] font-semibold text-white/30 uppercase tracking-widest cursor-pointer hover:text-white/60 transition-colors"
                onClick={() => toggleSort('name')}
              >
                <div className="flex items-center gap-2">
                  <span>Créateur</span>
                  {sortField === 'name' && (
                    sortDirection === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />
                  )}
                </div>
              </th>
              <th
                className="text-left py-4 px-8 text-[10px] font-semibold text-white/30 uppercase tracking-widest cursor-pointer hover:text-white/60 transition-colors"
                onClick={() => toggleSort('count')}
              >
                <div className="flex items-center gap-2">
                  <span>Collaborations</span>
                  {sortField === 'count' && (
                    sortDirection === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />
                  )}
                </div>
              </th>
              <th className="text-left py-4 px-8 text-[10px] font-semibold text-white/30 uppercase tracking-widest">
                Chiffre d'affaires
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/[0.04]">
            {influencerCollaborations.slice(0, 10).map((item, index) => (
              <tr
                key={index}
                className="hover:bg-white/[0.04] transition-colors cursor-pointer group"
                onClick={() => {
                  setSelectedInfluencer({
                    id: item.id,
                    name: item.name,
                    profile_image_url: item.profile_image_url
                  });
                  setIsDrawerOpen(true);
                }}
              >
                <td className="py-5 px-8">
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <InfluencerAvatar
                        profileImageUrl={item.profile_image_url}
                        name={item.name}
                        size="sm"
                      />
                      {index < 3 && (
                        <div className={`absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                          index === 0 ? 'bg-yellow-400 text-yellow-900' :
                          index === 1 ? 'bg-white/30 text-black' :
                          'bg-amber-600 text-white'
                        }`}>
                          {index + 1}
                        </div>
                      )}
                    </div>
                    <span className="font-medium text-white/80 group-hover:text-white transition-colors">
                      {item.name}
                    </span>
                  </div>
                </td>
                <td className="py-5 px-8">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-500/20 text-blue-400 rounded-lg font-semibold text-sm">
                    <CheckSquare className="w-4 h-4" />
                    {item.count}
                  </div>
                </td>
                <td className="py-5 px-8">
                  <span className="font-bold text-white">
                    {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(item.revenue)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {influencerCollaborations.length === 0 && (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-white/[0.06] rounded-2xl flex items-center justify-center mx-auto mb-4">
              <UserCheck className="w-8 h-8 text-white/30" />
            </div>
            <p className="text-white/40 font-medium">Aucun créateur trouvé</p>
          </div>
        )}
      </div>

      {/* Drawer pour les campagnes du créateur */}
      <Dialog
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        className="fixed inset-0 z-50"
      >
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setIsDrawerOpen(false)} />
        <div className="fixed inset-y-0 right-0 flex max-w-2xl w-full">
          <Dialog.Panel className="relative bg-[#111111] border-l border-white/[0.06] w-full flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-white/[0.06]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {selectedInfluencer && (
                    <>
                      <InfluencerAvatar
                        profileImageUrl={selectedInfluencer.profile_image_url}
                        name={selectedInfluencer.name}
                        size="md"
                      />
                      <div>
                        <h2 className="text-2xl font-bold text-white">
                          {selectedInfluencer.name}
                        </h2>
                        <p className="text-white/40 text-sm mt-1">
                          Campagnes de ce créateur
                        </p>
                      </div>
                    </>
                  )}
                </div>
                <button
                  onClick={() => setIsDrawerOpen(false)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6 text-white/60" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-4">
                {selectedInfluencer && collabs
                  .filter(collab => {
                    if (!Array.isArray(collab.influencers)) return false;
                    return collab.influencers.some(inf => {
                      const infFullName = (inf as any).display_name || (inf as any).full_name || inf.fullName;
                      return inf.id === selectedInfluencer.id ||
                             infFullName?.toLowerCase().trim() === selectedInfluencer.name.toLowerCase().trim();
                    });
                  })
                  .map((collab) => {
                    const influencerData = collab.influencers?.find(inf => {
                      const infFullName = (inf as any).display_name || (inf as any).full_name || inf.fullName;
                      return inf.id === selectedInfluencer.id ||
                             infFullName?.toLowerCase().trim() === selectedInfluencer.name.toLowerCase().trim();
                    });
                    const rate = parseFloat(influencerData?.rate || '0');

                    return (
                      <div
                        key={collab.id}
                        className="rounded-xl p-5 border border-white/[0.08] bg-white/[0.04] hover:border-white/[0.16] transition-all"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <h3 className="font-bold text-white text-lg mb-1">
                              {collab.name || 'Sans nom'}
                            </h3>
                            <p className="text-sm text-white/40">
                              {collab.brand || 'Client non défini'}
                            </p>
                          </div>
                          <div className="text-right">
                            <div className="text-xl font-bold text-blue-400">
                              {rate > 0 ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(rate) : '—'}
                            </div>
                            <p className="text-xs text-white/30 mt-1">Tarif</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/[0.06]">
                          <Calendar className="w-4 h-4 text-white/30" />
                          <span className="text-sm text-white/50">
                            {collab.date ? new Date(collab.date).toLocaleDateString('fr-FR', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric'
                            }) : 'Date non définie'}
                          </span>
                        </div>

                        <div className="flex items-center gap-2 mt-3">
                          {collab.payment_received && (
                            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-500/20 text-blue-400 rounded-lg text-xs font-semibold">
                              <Euro className="w-3.5 h-3.5" />
                              Payé
                            </div>
                          )}
                          {collab.contract_signed && !collab.payment_received && (
                            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-500/20 text-emerald-400 rounded-lg text-xs font-semibold">
                              <CheckSquare className="w-3.5 h-3.5" />
                              Signé
                            </div>
                          )}
                          {collab.quote_sent && !collab.contract_signed && (
                            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-500/20 text-amber-400 rounded-lg text-xs font-semibold">
                              <ClipboardCheck className="w-3.5 h-3.5" />
                              Devis envoyé
                            </div>
                          )}
                          {!collab.quote_sent && !collab.contract_signed && (
                            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/10 text-white/40 rounded-lg text-xs font-semibold">
                              <FileText className="w-3.5 h-3.5" />
                              En attente
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
  );
};

export default function Dashboard() {
  const { currentUser, userProfile } = useAuth();
  const [collabs, setCollabs] = useState<Collab[]>([]);
  const [influencers, setInfluencers] = useState<Influencer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCalendarModalOpen, setIsCalendarModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<'current' | 'all' | '3months' | 'custom'>('current');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [filteredCollabs, setFilteredCollabs] = useState<Collab[]>([]);
  const [calendarMonth, setCalendarMonth] = useState<Date>(new Date());

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (collabs.length > 0) {
      setFilteredCollabs(getFilteredCollabs());
    }
  }, [collabs, timeRange, calendarMonth]);

  const loadData = async () => {
    if (!currentUser || !userProfile?.agency_id) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const collabsData = await getDocuments<Collab>('collabs', userProfile.agency_id);

      const uniqueInfluencerIds = new Set<string>();
      collabsData.forEach(collab => {
        if (collab.influencer_ids && Array.isArray(collab.influencer_ids)) {
          collab.influencer_ids.forEach(id => uniqueInfluencerIds.add(id));
        }
      });

      const influencerIdsArray = Array.from(uniqueInfluencerIds);

      if (influencerIdsArray.length > 0) {
        const { data: influencersData, error: influencersError } = await supabase
          .from('influencers')
          .select('*')
          .in('id', influencerIdsArray);

        if (influencersError) {
          console.error('Error loading influencers:', influencersError);
          setInfluencers([]);
        } else {
          setInfluencers((influencersData || []).map(normalizeInfluencer));
        }
      } else {
        setInfluencers([]);
      }

      setCollabs(collabsData);
      setFilteredCollabs(getFilteredCollabs(collabsData, timeRange));
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setError('Une erreur est survenue lors du chargement des données');
    } finally {
      setIsLoading(false);
    }
  };

  const getFilteredCollabs = (data = collabs, range = timeRange) => {
    if (range === 'all') return data;

    const now = calendarMonth;
    let cutoffDate: Date;
    let endCutoffDate: Date | null = null;

    switch (range) {
      case 'current':
        // Filtrer par le mois du calendrier
        cutoffDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endCutoffDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
        break;
      case '3months':
        cutoffDate = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
        break;
      case 'custom':
        if (!startDate) return data;
        cutoffDate = new Date(startDate);
        break;
      default:
        return data;
    }

    let filtered = data.filter(collab => {
      const rawDate = (collab as any).signed_date || collab.date;
      if (!rawDate) return false;
      const collabDate = new Date(rawDate);

      if (endCutoffDate) {
        return collabDate >= cutoffDate && collabDate <= endCutoffDate;
      }

      return collabDate >= cutoffDate;
    });

    if (range === 'custom' && endDate) {
      const endCutoff = new Date(endDate);
      endCutoff.setHours(23, 59, 59);

      filtered = filtered.filter(collab => {
        const rawDate = (collab as any).signed_date || collab.date;
        if (!rawDate) return false;
        const collabDate = new Date(rawDate);

        return collabDate <= endCutoff;
      });
    }

    return filtered;
  };

  const formatDateRange = () => {
    const now = calendarMonth;

    switch (timeRange) {
      case 'current':
        return `Mois de ${now.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}`;
      case '3months':
        const threeMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
        return `${threeMonthsAgo.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })} - ${now.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}`;
      case 'custom':
        if (!startDate) return 'Période personnalisée';
        const start = new Date(startDate);
        const end = endDate ? new Date(endDate) : now;
        return `${start.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })} - ${end.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}`;
      default:
        return 'Toutes les périodes';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-white/10 border-t-white/60 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white/40 font-medium">Chargement du dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-4 mb-2">
              <div className="w-14 h-14 rounded-2xl bg-blue-500/20 flex items-center justify-center">
                <BarChart3 className="w-7 h-7 text-blue-400" />
              </div>
              <div>
                <h1 className="text-4xl font-bold text-white">
                  Dashboard
                </h1>
                <p className="text-white/40 mt-1">
                  Vue d'ensemble de votre activité
                </p>
              </div>
            </div>
          </div>
          <button
            onClick={() => setIsCalendarModalOpen(true)}
            className="flex items-center gap-3 px-6 py-3 bg-white/[0.06] border border-white/[0.08] rounded-xl hover:bg-white/[0.10] transition-all group"
          >
            <Filter className="w-5 h-5 text-white/50" />
            <span className="font-semibold text-white/70">{formatDateRange()}</span>
          </button>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-4 text-red-400">
            {error}
          </div>
        )}

        {/* Status Cards */}
        <CollaborationStatusCards collabs={filteredCollabs} />

        {/* Revenue Calendar */}
        <MonthlyRevenueCalendar
          collabs={collabs}
          timeRange={timeRange}
          startDate={startDate}
          endDate={endDate}
          onMonthChange={(month) => setCalendarMonth(month)}
        />

        {/* Charts row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <CollabTypeChart collabs={filteredCollabs} />
          <TopBrandsChart collabs={filteredCollabs} />
        </div>

        <InfluencerCollaborationsTable collabs={filteredCollabs} influencers={influencers} />
      </div>

      <Dialog
        open={isCalendarModalOpen}
        onClose={() => setIsCalendarModalOpen(false)}
        className="fixed inset-0 z-50 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="relative bg-[#141414] border border-white/[0.08] rounded-2xl max-w-md w-full mx-4 p-8">
            <h2 className="text-2xl font-bold text-white mb-6">
              Filtrer par période
            </h2>

            <div className="space-y-3">
              <button
                onClick={() => { setTimeRange('current'); setIsCalendarModalOpen(false); }}
                className={`w-full text-left px-5 py-4 rounded-xl border transition-all font-medium ${
                  timeRange === 'current'
                    ? 'bg-white text-black border-white'
                    : 'bg-white/[0.04] text-white/70 border-white/[0.08] hover:bg-white/[0.08]'
                }`}
              >
                Mois actuel
              </button>

              <button
                onClick={() => { setTimeRange('3months'); setIsCalendarModalOpen(false); }}
                className={`w-full text-left px-5 py-4 rounded-xl border transition-all font-medium ${
                  timeRange === '3months'
                    ? 'bg-white text-black border-white'
                    : 'bg-white/[0.04] text-white/70 border-white/[0.08] hover:bg-white/[0.08]'
                }`}
              >
                3 derniers mois
              </button>

              <button
                onClick={() => { setTimeRange('all'); setIsCalendarModalOpen(false); }}
                className={`w-full text-left px-5 py-4 rounded-xl border transition-all font-medium ${
                  timeRange === 'all'
                    ? 'bg-white text-black border-white'
                    : 'bg-white/[0.04] text-white/70 border-white/[0.08] hover:bg-white/[0.08]'
                }`}
              >
                Toutes les périodes
              </button>

              <div className="border-t border-white/[0.06] pt-6 mt-6">
                <h3 className="text-lg font-semibold text-white mb-4">
                  Période personnalisée
                </h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-[10px] font-semibold text-white/40 uppercase tracking-widest mb-2">
                      Date de début
                    </label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-white/[0.08] bg-white/[0.06] text-white focus:ring-1 focus:ring-white/20 outline-none transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-semibold text-white/40 uppercase tracking-widest mb-2">
                      Date de fin
                    </label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-white/[0.08] bg-white/[0.06] text-white focus:ring-1 focus:ring-white/20 outline-none transition-all"
                    />
                  </div>

                  <button
                    onClick={() => {
                      if (startDate) {
                        setTimeRange('custom');
                        setIsCalendarModalOpen(false);
                      }
                    }}
                    disabled={!startDate}
                    className={`w-full px-5 py-4 rounded-xl border transition-all font-medium ${
                      !startDate
                        ? 'bg-white/[0.04] text-white/20 border-white/[0.06] cursor-not-allowed'
                        : timeRange === 'custom'
                          ? 'bg-white text-black border-white'
                          : 'bg-white/[0.04] text-white/70 border-white/[0.08] hover:bg-white/[0.08]'
                    }`}
                  >
                    Appliquer
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
